import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Divider,
  Group,
  Menu,
  ScrollArea,
  Select,
  Stack,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import { IconDotsVertical, IconEdit, IconTrash } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { ADDENDUM_REASON_OPTIONS, AddendumTrackedChangesView } from '../../appointments/addendums';
import type { AdminNote, ClinicalNote } from '../data/types';
import { getSectionsForNoteType, type NoteType } from '../data/noteTypeSections';

function formatDate(dateString: string): string {
  if (!dateString) return '--';
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      dateStyle: 'medium',
    });
  } catch {
    return dateString;
  }
}

/** Note status tag colors: Signed=blue, Note/Complete=green, Draft=yellow, etc. */
function getNoteStatusColor(status: string): string {
  const colorMap: Record<string, string> = {
    Signed: 'blue',
    Complete: 'green',
    Draft: 'yellow',
    Locked: 'teal',
    'Pending co-signature': 'orange',
    Private: 'gray',
  };
  return colorMap[status] || 'gray';
}

function getBillingStatusColor(status: string): string {
  const colorMap: Record<string, string> = {
    Billed: 'green',
    Unbilled: 'orange',
    Pending: 'yellow',
    'N/A': 'gray',
  };
  return colorMap[status] || 'gray';
}

function formatAdminNoteDate(dateString: string): string {
  if (!dateString) return '--';
  try {
    return new Date(dateString).toLocaleDateString('en-US', { dateStyle: 'medium' });
  } catch {
    return dateString;
  }
}

/** Note types that use editable fields in the detail modal and have no addendums. */
const EDITABLE_NOTE_TYPES: NoteType[] = ['Chart Note', 'Chart Notes', 'Risk Assessment', 'Safety Plan'];

interface NoteDetailViewProps {
  note: ClinicalNote;
  /** Admin notes attached to this note (medical record row) */
  adminNotes?: AdminNote[];
  /** Optional footer (e.g. Close button) */
  footer?: React.ReactNode;
  /** Callback to add an addendum (noteId, content, reasonForChange). Corrected note = primary content + addendums; export uses corrected version only. */
  onAddAddendum?: (noteId: string, content: string, reasonForChange?: string) => void;
  /** Callback to save edited soap content (for Chart Note, Risk Assessment, Safety Plan) */
  onUpdateNote?: (noteId: string, soapNote: NonNullable<ClinicalNote['soapNote']>) => void;
  /** Called after saving editable note to close the modal. */
  onSaveAndClose?: () => void;
  /** Callbacks for admin note management (when provided, show edit/delete on each admin note) */
  onEditAdminNote?: (note: AdminNote) => void;
  onDeleteAdminNote?: (note: AdminNote) => void;
}

/**
 * Full chart note detail view: metadata, signatures, and SOAP content.
 * Use in modals or panels for viewing a single ClinicalNote.
 */
export function NoteDetailView({
  note,
  adminNotes = [],
  footer,
  onAddAddendum,
  onUpdateNote,
  onSaveAndClose,
  onEditAdminNote,
  onDeleteAdminNote,
}: NoteDetailViewProps): JSX.Element {
  const soapRows = 6;
  const [addendumContent, setAddendumContent] = useState('');
  const [addendumReason, setAddendumReason] = useState<string | null>(null);
  const [showAddendumForm, setShowAddendumForm] = useState(false);
  const isEditableType = EDITABLE_NOTE_TYPES.includes(note.noteType);
  const showAddendums = !isEditableType;
  const initialSoap = note.soapNote ?? { subjective: '', objective: '', assessment: '', plan: '' };
  const [editableSoap, setEditableSoap] = useState(initialSoap);
  useEffect(() => {
    setEditableSoap(note.soapNote ?? { subjective: '', objective: '', assessment: '', plan: '' });
  }, [note.id, note.soapNote]);
  const handleEditableChange = (key: keyof typeof editableSoap, value: string) => {
    setEditableSoap((prev) => ({ ...prev, [key]: value }));
  };
  const handleSaveEditable = () => {
    onUpdateNote?.(note.id, editableSoap);
    onSaveAndClose?.();
  };

  return (
    <ScrollArea.Autosize mah={600} type="scroll" offsetScrollbars>
      <Stack gap="md" pr="xs">
        {/* Header */}
        <Group justify="space-between" wrap="nowrap">
          <div>
            <Title order={4}>{note.title}</Title>
            <Text size="sm" c="gray.6">
              {note.noteType} • {formatDate(note.dateOfService)}
            </Text>
          </div>
          <Group gap="xs">
            <Badge color={getNoteStatusColor(note.noteStatus)} variant="light" size="sm">
              {note.noteStatus}
            </Badge>
            <Badge color={getBillingStatusColor(note.billingStatus)} variant="light" size="sm">
              {note.billingStatus}
            </Badge>
          </Group>
        </Group>

        {/* Metadata grid */}
        <Stack gap="xs">
          <Text size="xs" c="gray.6" tt="uppercase" fw={600}>Note information</Text>
          <Group grow style={{ alignItems: 'flex-start' }}>
            <div>
              <Text size="xs" c="gray.6">Date of service</Text>
              <Text size="sm">{formatDate(note.dateOfService)}</Text>
            </div>
            <div>
              <Text size="xs" c="gray.6">Note date</Text>
              <Text size="sm">{formatDate(note.date)}</Text>
            </div>
            <div>
              <Text size="xs" c="gray.6">Note type</Text>
              <Text size="sm">{note.noteType}</Text>
            </div>
            <div>
              <Text size="xs" c="gray.6">Appointment type</Text>
              <Text size="sm">{note.appointmentType}</Text>
            </div>
          </Group>
        </Stack>

        <Divider />

        <Stack gap="xs">
          <Text size="xs" c="gray.6" tt="uppercase" fw={600}>Provider & service</Text>
          <div>
            <Text size="xs" c="gray.6">Provider</Text>
            <Text size="sm">{note.provider} ({note.providerType})</Text>
          </div>
          <div>
            <Text size="xs" c="gray.6">Service type</Text>
            <Text size="sm">{note.serviceType}</Text>
          </div>
          <div>
            <Text size="xs" c="gray.6">Service rendered</Text>
            <Text size="sm">{note.serviceRendered}</Text>
          </div>
        </Stack>

        {note.cptCodes && note.cptCodes.length > 0 && (
          <>
            <Divider />
            <div>
              <Text size="xs" c="gray.6" tt="uppercase" fw={600}>CPT codes</Text>
              <Group gap="xs" mt={4}>
                {note.cptCodes.map((code) => (
                  <Badge key={code} variant="outline" size="sm">
                    {code}
                  </Badge>
                ))}
              </Group>
            </div>
          </>
        )}

        {(note.template || note.isAutoGenerated !== undefined || note.requiresSignOff !== undefined) && (
          <>
            <Divider />
            <Stack gap="xs">
              <Text size="xs" c="gray.6" tt="uppercase" fw={600}>Documentation</Text>
              {note.template && (
                <div>
                  <Text size="xs" c="gray.6">Template</Text>
                  <Text size="sm">{note.template}</Text>
                </div>
              )}
              {note.isAutoGenerated !== undefined && (
                <div>
                  <Text size="xs" c="gray.6">Auto-generated</Text>
                  <Text size="sm">{note.isAutoGenerated ? 'Yes' : 'No'}</Text>
                </div>
              )}
              {note.requiresSignOff !== undefined && (
                <div>
                  <Text size="xs" c="gray.6">Requires sign-off</Text>
                  <Text size="sm">{note.requiresSignOff ? 'Yes' : 'No'}</Text>
                </div>
              )}
            </Stack>
          </>
        )}

        {(note.signedBy || note.signedDate || note.reviewedBy || note.reviewedDate) && (
          <>
            <Divider />
            <Stack gap="xs">
              <Text size="xs" c="gray.6" tt="uppercase" fw={600}>Signatures & review</Text>
              <Group grow style={{ alignItems: 'flex-start' }}>
                {note.signedBy && (
                  <div>
                    <Text size="xs" c="gray.6">Signed by</Text>
                    <Text size="sm">{note.signedBy}</Text>
                  </div>
                )}
                {note.signedDate && (
                  <div>
                    <Text size="xs" c="gray.6">Signed date</Text>
                    <Text size="sm">{formatDate(note.signedDate)}</Text>
                  </div>
                )}
                {note.reviewedBy && (
                  <div>
                    <Text size="xs" c="gray.6">Reviewed by</Text>
                    <Text size="sm">{note.reviewedBy}</Text>
                  </div>
                )}
                {note.reviewedDate && (
                  <div>
                    <Text size="xs" c="gray.6">Reviewed date</Text>
                    <Text size="sm">{formatDate(note.reviewedDate)}</Text>
                  </div>
                )}
              </Group>
            </Stack>
          </>
        )}

        {note.soapNote && (
          <>
            <Divider />
            <Stack gap="md">
              {note.appointmentId && !isEditableType ? (
                (() => {
                  const config = getSectionsForNoteType(note.noteType);
                  return (
                    <>
                      <Text size="xs" c="gray.6" tt="uppercase" fw={600}>{config.heading}</Text>
                      {config.sections.map(({ label, key }) => (
                        <div key={key}>
                          <Text size="xs" c="gray.6" mb={4} fw={500}>{label}</Text>
                          <Textarea
                            value={note.soapNote?.[key] ?? ''}
                            readOnly
                            minRows={soapRows}
                            autosize
                            maxRows={20}
                            styles={{ input: { backgroundColor: 'var(--mantine-color-default-hover)' } }}
                          />
                        </div>
                      ))}
                    </>
                  );
                })()
              ) : isEditableType && onUpdateNote ? (
                (() => {
                  const config = getSectionsForNoteType(note.noteType);
                  return (
                    <>
                      <Text size="xs" c="gray.6" tt="uppercase" fw={600}>{config.heading}</Text>
                      {config.sections.map(({ label, key }) => (
                        <div key={key}>
                          <Text size="xs" c="gray.6" mb={4} fw={500}>{label}</Text>
                          <Textarea
                            value={editableSoap[key] ?? ''}
                            onChange={(e) => handleEditableChange(key, e.currentTarget.value)}
                            minRows={soapRows}
                            autosize
                            maxRows={20}
                          />
                        </div>
                      ))}
                      <Group justify="space-between">
                        <Button size="sm" variant="default" onClick={onSaveAndClose}>
                          Close
                        </Button>
                        <Button size="sm" onClick={handleSaveEditable}>
                          Save changes
                        </Button>
                      </Group>
                    </>
                  );
                })()
              ) : (
                (() => {
                  const config = getSectionsForNoteType(note.noteType);
                  const typeDescriptions: Partial<Record<NoteType, string>> = {
                    'Chart Note': 'General clinical documentation not tied to an appointment.',
                    'Chart Notes': 'General clinical documentation not tied to an appointment.',
                    'Risk Assessment': 'Documentation of presenting concerns, risk and protective factors, clinical assessment, and recommendations.',
                    'Safety Plan': 'Documentation of warning signs, coping strategies, safe environment, and crisis contacts.',
                  };
                  const description = typeDescriptions[note.noteType];
                  return (
                    <>
                      <Text size="xs" c="gray.6" tt="uppercase" fw={600}>{config.heading}</Text>
                      {description && (
                        <Text size="sm" c="gray.6" mb="sm">{description}</Text>
                      )}
                      {config.sections.map(({ label, key }) => (
                        <div key={key}>
                          <Text size="xs" c="gray.6" mb={4} fw={500}>{label}</Text>
                          <Textarea
                            value={note.soapNote?.[key] ?? ''}
                            readOnly
                            minRows={soapRows}
                            autosize
                            maxRows={20}
                            styles={{ input: { backgroundColor: 'var(--mantine-color-default-hover)' } }}
                          />
                        </div>
                      ))}
                    </>
                  );
                })()
              )}
            </Stack>
          </>
        )}

        {!note.soapNote && (
          <>
            <Divider />
            <Text size="sm" c="gray.6">No SOAP content recorded for this note.</Text>
          </>
        )}

        {/* Corrected note: addendum content (forward-facing) with highlight + change history */}
        {showAddendums && (note.addendums?.length ?? 0) > 0 && (
          <>
            <Divider />
            <Stack gap="md">
              <Text size="xs" c="gray.6" tt="uppercase" fw={600}>Addendum content</Text>
              {note.addendums?.map((add) => (
                <Box
                  key={add.id}
                  p="sm"
                  style={{
                    borderRadius: 'var(--mantine-radius-sm)',
                    backgroundColor: 'var(--mantine-color-amber-0)',
                    border: '1px solid var(--mantine-color-amber-2)',
                    borderLeft: '3px solid var(--mantine-color-amber-5)',
                  }}
                >
                  <Text size="xs" c="gray.6" mb={4}>
                    {formatAdminNoteDate(add.createdAt)} — {add.createdBy}
                  </Text>
                  {add.lineChanges?.length ? (
                    <AddendumTrackedChangesView
                      lineChanges={add.lineChanges}
                      comments={add.content?.trim() ? add.content : undefined}
                    />
                  ) : (
                    <Text size="sm" c="gray.8" fw={500} style={{ whiteSpace: 'pre-wrap' }}>
                      {add.content}
                    </Text>
                  )}
                </Box>
              ))}
            </Stack>
            <Box
              mt="md"
              pt="md"
              style={{
                borderTop: '1px solid var(--mantine-color-gray-3)',
                backgroundColor: 'var(--mantine-color-gray-0)',
                borderRadius: 'var(--mantine-radius-md)',
                padding: 'var(--mantine-spacing-md)',
              }}
            >
              <Title order={5} size="sm" c="gray.7" mb="sm" tt="uppercase" fw={600}>
                Change history
              </Title>
              <Text size="xs" c="gray.6" mb="md">
                Full addendum history. Session duration and encounter-level metadata are not modified by addendums.
              </Text>
              <Stack gap="sm">
                {note.addendums?.map((add) => {
                  const reasonLabel = add.reasonForChange
                    ? (ADDENDUM_REASON_OPTIONS.find((o) => o.value === add.reasonForChange)?.label ?? add.reasonForChange)
                    : '—';
                  return (
                    <Box
                      key={add.id}
                      p="sm"
                      style={{
                        borderRadius: 'var(--mantine-radius-sm)',
                        border: '1px solid var(--mantine-color-gray-2)',
                        backgroundColor: 'var(--mantine-color-white)',
                      }}
                    >
                      <Stack gap={4}>
                        <Group gap="md" wrap="wrap">
                          <Text size="xs" c="gray.6">
                            Original note: {formatDate(note.dateOfService)}
                          </Text>
                          <Text size="xs" c="gray.6">
                            Addendum: {formatAdminNoteDate(add.createdAt)}
                          </Text>
                        </Group>
                        <Text size="xs" c="gray.7">
                          Author: {add.createdBy}
                        </Text>
                        <Text size="xs" c="gray.7">
                          Reason: {reasonLabel}
                        </Text>
                        <AddendumTrackedChangesView
                          lineChanges={add.lineChanges}
                          comments={add.lineChanges?.length ? add.content : undefined}
                          fallbackBody={add.lineChanges?.length ? undefined : add.content}
                        />
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          </>
        )}

        {showAddendums && onAddAddendum && (
          <>
            <Divider />
            <Stack gap="xs">
              <Text size="xs" c="gray.6" tt="uppercase" fw={600}>Add addendum</Text>
              {showAddendumForm ? (
                <Stack gap="xs">
                  <Select
                    label="Reason for change"
                    placeholder="Select a reason"
                    data={ADDENDUM_REASON_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                    value={addendumReason}
                    onChange={(v) => setAddendumReason(v)}
                    required
                  />
                  <Textarea
                    placeholder="Enter addendum content..."
                    value={addendumContent}
                    onChange={(e) => setAddendumContent(e.currentTarget.value)}
                    minRows={3}
                  />
                  <Group gap="xs">
                    <Button
                      size="xs"
                      variant="default"
                      onClick={() => {
                        setShowAddendumForm(false);
                        setAddendumContent('');
                        setAddendumReason(null);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="xs"
                      onClick={() => {
                        if (addendumContent.trim()) {
                          onAddAddendum(note.id, addendumContent.trim(), addendumReason ?? undefined);
                          setAddendumContent('');
                          setAddendumReason(null);
                          setShowAddendumForm(false);
                        }
                      }}
                      disabled={!addendumContent.trim() || !addendumReason}
                    >
                      Save addendum
                    </Button>
                  </Group>
                </Stack>
              ) : (
                <Button variant="light" size="sm" onClick={() => setShowAddendumForm(true)}>
                  Add addendum
                </Button>
              )}
            </Stack>
          </>
        )}

        {adminNotes.length > 0 && (
          <>
            <Divider />
            <Stack gap="xs">
              <Text size="xs" c="gray.6" tt="uppercase" fw={600}>Admin notes</Text>
              {adminNotes.map((adminNote) => (
                <Group key={adminNote.id} p="sm" align="flex-start" wrap="nowrap" style={{ backgroundColor: 'var(--mantine-color-gray-0)', borderRadius: 'var(--mantine-radius-sm)' }}>
                  <Badge variant="light" size="sm">{adminNote.type}</Badge>
                  <Text size="sm" style={{ flex: 1, minWidth: 0 }}>{adminNote.content}</Text>
                  <Group gap={4} wrap="nowrap">
                    <Text size="xs" c="gray.6">{formatAdminNoteDate(adminNote.createdAt)} — {adminNote.createdBy}</Text>
                    {(onEditAdminNote || onDeleteAdminNote) && (
                      <Menu position="bottom-end" withArrow>
                        <Menu.Target>
                          <ActionIcon variant="subtle" size="sm">
                            <IconDotsVertical size={14} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          {onEditAdminNote && (
                            <Menu.Item leftSection={<IconEdit size={14} />} onClick={() => onEditAdminNote(adminNote)}>
                              Edit
                            </Menu.Item>
                          )}
                          {onDeleteAdminNote && (
                            <Menu.Item leftSection={<IconTrash size={14} />} color="red" onClick={() => onDeleteAdminNote(adminNote)}>
                              Delete
                            </Menu.Item>
                          )}
                        </Menu.Dropdown>
                      </Menu>
                    )}
                  </Group>
                </Group>
              ))}
            </Stack>
          </>
        )}

        {footer}
      </Stack>
    </ScrollArea.Autosize>
  );
}
